import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Region, Warehouse } from '../types/warehouse';
import { getAllRegions, getCurrentWarehouse, setCurrentWarehouse } from '../services/warehouseService';
import './WarehouseSelector.scss';

const WarehouseSelector: React.FC = () => {
    const navigate = useNavigate();
    const [currentWh, setCurrentWh] = useState<Warehouse>(getCurrentWarehouse());
    const [regions, setRegions] = useState<Region[]>([]);
    const [inputValue, setInputValue] = useState(getCurrentWarehouse().warehouseId);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => { loadRegions(); }, []);

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
                inputRef.current && !inputRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
                setInputValue(getCurrentWarehouse().warehouseId);
                inputRef.current?.blur();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const loadRegions = async () => {
        try {
            setIsLoading(true);
            const data = await getAllRegions();
            setRegions(data);
        } catch (error) {
            console.error('Failed to load regions:', error);
        } finally {
            setIsLoading(false);
        }
    };

    /** Filter regions — keep only regions that have matching warehouses */
    const getFilteredRegions = (): Region[] => {
        const term = inputValue.trim().toUpperCase();
        if (!term) return regions;
        return regions
            .map(r => ({
                ...r,
                warehouses: r.warehouses.filter(w => w.warehouseId.toUpperCase().includes(term)),
            }))
            .filter(r => r.warehouses.length > 0);
    };

    const handleSelect = (wh: Warehouse) => {
        setCurrentWh(wh);
        setCurrentWarehouse(wh);
        setInputValue(wh.warehouseId);
        setShowDropdown(false);
        // Navigate to /:warehouseId — preserves the current page under the new warehouse
        const currentPath = window.location.pathname;
        // Strip any existing warehouse prefix from the path
        const pathWithoutWarehouse = currentPath.replace(/^\/[A-Za-z]{2,5}[0-9]{1,3}(\/|$)/, '/');
        const normalizedPath = pathWithoutWarehouse === '/' ? '' : pathWithoutWarehouse;
        navigate(`/${wh.warehouseId}${normalizedPath}`);
        window.dispatchEvent(new Event('warehouseChanged'));
    };

    const handleFocus = () => {
        setShowDropdown(true);
        setInputValue('');
        setSelectedRegion(null);
    };

    const handleBlur = () => {
        setTimeout(() => {
            // Only reset input if dropdown is no longer visible
            if (!showDropdown) {
                setInputValue(currentWh.warehouseId);
            }
        }, 200);
    };

    /** Prevent mousedown inside dropdown from stealing focus from input */
    const handleDropdownMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
    };

    /** Find a warehouse by exact (case-insensitive) warehouseId match across all regions. */
    const findExactMatch = (term: string): Warehouse | undefined => {
        const normalized = term.trim().toUpperCase();
        if (!normalized) return undefined;
        for (const region of regions) {
            const match = region.warehouses.find(w => w.warehouseId.toUpperCase() === normalized);
            if (match) return match;
        }
        return undefined;
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setShowDropdown(false);
            setInputValue(currentWh.warehouseId);
            inputRef.current?.blur();
        } else if (e.key === 'Enter') {
            // Matches old-website behavior: typing a warehouse ID and hitting Enter
            // selects it directly, without needing to click the suggestion.
            const match = findExactMatch(inputValue);
            if (match) {
                e.preventDefault();
                handleSelect(match);
                inputRef.current?.blur();
            }
        }
    };

    const filteredRegions = getFilteredRegions();

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <input
                ref={inputRef}
                type="text"
                className="form-control"
                placeholder="e.g. IND1"
                value={inputValue}
                onChange={(e) => { setInputValue(e.target.value); setShowDropdown(true); }}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                aria-label="Warehouse ID"
                style={{
                    width: '100px',
                    height: '30px',
                    fontSize: '13px',
                    padding: '2px 8px',
                    marginTop: '10px',
                    fontWeight: 'bold',
                }}
            />
            {showDropdown && (
                <div
                    ref={dropdownRef}
                    onMouseDown={handleDropdownMouseDown}
                    style={{
                        position: 'absolute',
                        top: '42px',
                        left: 0,
                        zIndex: 2000,
                        background: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        minWidth: '280px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        padding: '8px',
                    }}
                >
                    {isLoading ? (
                        <div style={{ padding: '10px', color: '#999' }}>
                            <i className="fa fa-spinner fa-spin"></i> Loading...
                        </div>
                    ) : filteredRegions.length === 0 ? (
                        <div style={{ padding: '10px', color: '#999' }}>No matches</div>
                    ) : (
                        <>
                        {/* Region tabs — horizontal row, clickable */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '10px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>
                            {filteredRegions.map(region => (
                                <span
                                    key={region.regionCode}
                                    onClick={() => setSelectedRegion(
                                        selectedRegion === region.regionCode ? null : region.regionCode
                                    )}
                                    style={{
                                        display: 'inline-block',
                                        padding: '5px 14px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        borderRadius: '3px',
                                        background: selectedRegion === region.regionCode ? '#337ab7' : '#e0e0e0',
                                        color: selectedRegion === region.regionCode ? 'white' : '#333',
                                        userSelect: 'none',
                                    }}
                                >
                                    {region.regionCode}
                                </span>
                            ))}
                        </div>
                        {/* Warehouses — show selected region or all */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {filteredRegions
                                .filter(r => !selectedRegion || r.regionCode === selectedRegion)
                                .reduce<Warehouse[]>((acc, region) => acc.concat(region.warehouses), [])
                                .map(wh => (
                                    <span
                                        key={wh.warehouseId}
                                        onClick={() => handleSelect(wh)}
                                        style={{
                                            display: 'inline-block',
                                            padding: '3px 8px',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            borderRadius: '3px',
                                            background: currentWh.warehouseId === wh.warehouseId ? '#337ab7' : '#f0f0f0',
                                            color: currentWh.warehouseId === wh.warehouseId ? 'white' : '#333',
                                            fontWeight: currentWh.warehouseId === wh.warehouseId ? 'bold' : 'normal',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (currentWh.warehouseId !== wh.warehouseId) e.currentTarget.style.background = '#ddd';
                                        }}
                                        onMouseLeave={(e) => {
                                            if (currentWh.warehouseId !== wh.warehouseId) e.currentTarget.style.background = '#f0f0f0';
                                        }}
                                    >
                                        {wh.warehouseId}
                                    </span>
                                ))
                            }
                        </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default WarehouseSelector;
